import { ChangeEvent, FunctionComponent, useCallback, useMemo, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { faChevronDown, faChevronUp } from "@fortawesome/pro-regular-svg-icons";

import Link from "@mui/material/Link";

import { useTranslation } from "react-i18next";

import TextField from "@mui/material/TextField";

import Slider from "@mui/material/Slider";

import { Modal } from "@/components";
import { SearchField } from "@/components/SearchField/SearchField";
import { InputField } from "@/components/InputField/InputField";
import { Radio } from "@/components/Radio/Radio";
import { Button } from "@/components/Button/Button";
import { ButtonColor } from "@/components/Button/types";
import { MultiAutoComplete } from "@/components/MultiAutoComplete/MultiAutoComplete";
import {
	MultiAutoCompleteApi,
	MultiAutoCompleteApiItem,
} from "@/components/MultiAutoCompleteApi/MultiAutoCompleteApi";
import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";
import { classNames } from "@/utils/classNames";
import { Icon } from "@/components/Icon/Icon";

import {
	useAddManualOrganizationToTrackerMutation,
	useAddOrganizationToTrackerMutation,
} from "@/pages/Private/redux/profileTracker/profileTracker.api";
import {
	useGetGeoCountriesMutation,
	useGetGeoRegionsQuery,
	useGetIndustriesQuery,
	usePreviewCompanySearchMutation,
} from "@/pages/Private/redux/companyEnrich/companyEnrich.api";
import {
	CompanyFilterSearch,
	CompanySearchPreviewItem,
	Industry,
} from "@/pages/Private/pages/CompanyEnrich/schema/companyEnrich";

import { CompanyEnrichAutoComplete } from "@/pages/Private/pages/CompanyEnrich/components/CompanyEnrichAutoComplete";

import { ExternalLink } from "@/components/ExternalLink/ExternalLink";

import { SelectField } from "@/components/SelectField/SelectField";
import {
	WORKFORCE_DEPARTMENT_OPTIONS,
	WORKFORCE_PERIOD_OPTIONS,
} from "@/pages/Private/pages/CompanyEnrich/components/WorkforceGrowthFiltersSection";

import { ManualCompany, ManualCompanySchema } from "../../../schema/manual-company.interface";

export interface SearchForCompanyModalProps {
	open: boolean;
	setOpen: (value: boolean) => void;
	trackerId: number;
}

export const SearchForCompanyModal: FunctionComponent<SearchForCompanyModalProps> = ({
	open,
	setOpen,
	trackerId,
}) => {
	const [addCompanyType, setAddCompanyType] = useState<"enrich" | "manual">("enrich");
	const { t } = useTranslation();
	const ts = (key: string) => t(`companyEnrich.${key}`);

	const EMPLOYEE_OPTIONS = [
		{ id: "1-10", title: "1–10" },
		{ id: "11-50", title: "11–50" },
		{ id: "51-200", title: "51–200" },
		{ id: "201-500", title: "201–500" },
		{ id: "501-1K", title: "501–1,000" },
		{ id: "1K-5K", title: "1,001–5,000" },
		{ id: "5K-10K", title: "5,001–10,000" },
		{ id: "over-10K", title: "10,001+" },
	];
	const CATEGORY_OPTIONS = [
		{ id: "b2b", title: ts("categoryB2b") },
		{ id: "b2c", title: ts("categoryB2c") },
		{ id: "b2g", title: ts("categoryB2g") },
		{ id: "e-commerce", title: ts("categoryEcommerce") },
		{ id: "media", title: ts("categoryMedia") },
		{ id: "service-provider", title: ts("categoryServiceProvider") },
		{ id: "mobile", title: ts("categoryMobile") },
		{ id: "saas", title: ts("categorySaas") },
	];

	const FUNDING_ROUNDS_OPTIONS = [
		{ id: "seed", title: ts("fundingRoundSeed") },
		{ id: "debt_financing", title: ts("fundingRoundDebtFinancing") },
		{ id: "angel", title: ts("fundingRoundAngel") },
		{ id: "venture", title: ts("fundingRoundVenture") },
		{ id: "series_a", title: ts("fundingRoundSeriesA") },
		{ id: "series_b", title: ts("fundingRoundSeriesB") },
		{ id: "series_c", title: ts("fundingRoundSeriesC") },
		{ id: "series_d", title: ts("fundingRoundSeriesD") },
		{ id: "series_e", title: ts("fundingRoundSeriesE") },
		{ id: "series_f", title: ts("fundingRoundSeriesF") },
		{ id: "series_g", title: ts("fundingRoundSeriesG") },
		{ id: "series_h", title: ts("fundingRoundSeriesH") },
		{ id: "other", title: ts("fundingRoundOther") },
	];

	const COMPANY_TYPE_OPTIONS = [
		{ id: "private", title: ts("companyTypePrivate") },
		{ id: "public", title: ts("companyTypePublic") },
		{ id: "self-employed", title: ts("companyTypeSelfEmployed") },
		{ id: "self-owned", title: ts("companyTypeSelfOwned") },
		{ id: "partnership", title: ts("companyTypePartnership") },
		{ id: "non-profit", title: ts("companyTypeNonProfit") },
		{ id: "educational", title: ts("companyTypeEducational") },
		{ id: "government", title: ts("companyTypeGovernment") },
	];

	const REVENUE_OPTIONS = [
		{ id: "under-1m", title: ts("revenueUnder1m") },
		{ id: "1m-10m", title: ts("revenue1mTo10m") },
		{ id: "10m-50m", title: ts("revenue10mTo50m") },
		{ id: "50m-100m", title: ts("revenue50mTo100m") },
		{ id: "100m-200m", title: ts("revenue100mTo200m") },
		{ id: "200m-1b", title: ts("revenue200mTo1b") },
		{ id: "over-1b", title: ts("revenueOver1b") },
	];

	// Collapsible sections state
	const [showCompanyDetails, setShowCompanyDetails] = useState<boolean>(true);
	const [showFundingTimeline, setShowFundingTimeline] = useState<boolean>(false);
	const [showLocation, setShowLocation] = useState<boolean>(false);
	const [showKeywords, setShowKeywords] = useState<boolean>(false);
	const [showWorkforceGrowth, setShowWorkforceGrowth] = useState<boolean>(false);
	// Filter state
	const [query, setQuery] = useState("");
	const [employees, setEmployees] = useState<string[]>([]);
	const [keywords, setKeywords] = useState<string[]>([]);
	const [regions, setRegions] = useState<string[]>([]);
	const [countries, setCountries] = useState<string[]>([]);
	const [categories, setCategories] = useState<string[]>([]);
	const [type, setType] = useState<string[]>([]);
	const [revenue, setRevenue] = useState<string[]>([]);
	const [fundingRounds, setFundingRounds] = useState<string[]>([]);
	const [selectedIndustries, setSelectedIndustries] = useState<Industry[]>([]);

	// Range filter state
	const [foundedYearMin, setFoundedYearMin] = useState<number | null>(null);
	const [foundedYearMax, setFoundedYearMax] = useState<number | null>(null);
	const [fundingAmountMin, setFundingAmountMin] = useState<number | null>(null);
	const [fundingAmountMax, setFundingAmountMax] = useState<number | null>(null);
	const [fundingYearMin, setFundingYearMin] = useState<number | null>(null);
	const [fundingYearMax, setFundingYearMax] = useState<number | null>(null);
	const [workforcePeriod, setWorkforcePeriod] = useState<string | null>(null);
	const [workforceDepartment, setWorkforceDepartment] = useState<string | null>(null);
	const [workforceMinPercent, setWorkforceMinPercent] = useState<number | null>(null);
	const [workforceMaxPercent, setWorkforceMaxPercent] = useState<number | null>(null);

	// Results & selection state
	const [results, setResults] = useState<CompanySearchPreviewItem[]>([]);
	const [hasSearched, setHasSearched] = useState(false);
	const [selectedCompanies, setSelectedCompanies] = useState<CompanySearchPreviewItem[]>([]);

	// Manual form state
	const [currentFormState, setCurrentFormState] = useState<Partial<ManualCompany>>();
	const [workforceError, setWorkforceError] = useState<string | null>(null);
	const {
		handleSubmit,
		setValue,
		formState: { errors },
		reset,
	} = useForm<ManualCompany>({
		resolver: zodResolver(ManualCompanySchema),
	});

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];

	type WorkforceGrowth = NonNullable<CompanyFilterSearch["workforceGrowth"]>;
	type WorkforcePeriod = WorkforceGrowth["period"];
	type WorkforceDepartment = WorkforceGrowth["department"];

	const handleSaveFormState = (data: { value: string; name: keyof ManualCompany }[]) => {
		const newState = { ...currentFormState };

		data.forEach((item) => {
			newState[item.name] = item.value;
		});
		setCurrentFormState(newState);
		data.forEach((item) => {
			setValue(item.name, item.value, { shouldDirty: true, shouldValidate: true });
		});
	};

	const [previewCompanySearch, { isLoading: isSearchLoading }] = usePreviewCompanySearchMutation();

	const { data: geoRegionsData } = useGetGeoRegionsQuery();
	const [getGeoCountries] = useGetGeoCountriesMutation();
	const { data: industriesData } = useGetIndustriesQuery();
	const industryOptions = (industriesData || []).map((item) => ({
		id: item.name,
		title: item.name,
	}));

	const [addCompaniesToTracker, { isLoading: addCompaniesIsLoading }] =
		useAddOrganizationToTrackerMutation();

	const [
		addManualOrganizationToTracker,
		{ isLoading: addManualOrganizationIsLoading, error: addManualOrganizationError },
	] = useAddManualOrganizationToTrackerMutation();

	const regionOptions = (geoRegionsData?.items || []).map((item) => ({
		id: item.name,
		title: item.name,
	}));

	const geoRegions = geoRegionsData?.items || [];
	const countryNameMapRef = useRef<Map<string, string>>(new Map());

	const handleRegionSelect = useCallback(
		async (value?: { id: string | number; title: string }[]) => {
			const selectedRegionNames = value?.map((r) => r.id.toString()) || [];

			setRegions(selectedRegionNames);

			const countryCodes = new Set<string>(countries || []);

			for (const regionName of selectedRegionNames) {
				const region = geoRegions.find((r) => r.name === regionName);

				if (region) {
					region.countries.forEach((code) => countryCodes.add(code));
				}
			}

			const newCodes = [...countryCodes];

			// Fetch names for any codes we don't have yet
			const unknownCodes = newCodes.filter((code) => !countryNameMapRef.current?.has(code));

			if (unknownCodes.length > 0) {
				const allCountries = await getGeoCountries({ query: "" }).unwrap();

				for (const country of allCountries.items) {
					countryNameMapRef.current?.set(country.code, country.name);
				}
			}

			setCountries(newCodes);
		},
		[countries, geoRegions, getGeoCountries]
	);

	const handleCountrySelect = useCallback(
		async (value?: { id: string | number; title: string }[]) => {
			const codes = value?.map((r) => r.id.toString()) || [];

			// Update the country name map
			value?.forEach((r) => countryNameMapRef.current?.set(r.id.toString(), r.title));

			setCountries(codes);
		},
		[]
	);

	const fetchCompanyCountries = useCallback(
		async (query: string) => {
			const result = await getGeoCountries({ query }).unwrap();

			return result.items.map((item) => ({ id: item.code, title: item.name }));
		},
		[getGeoCountries]
	);

	const CURRENT_YEAR = new Date().getFullYear();

	const FOUNDING_YEAR_OPTIONS = [
		{ title: "Any", value: null },
		...Array.from({ length: CURRENT_YEAR - 1969 }, (_, i) => ({
			title: String(1970 + i),
			value: 1970 + i,
		})),
	];

	const FUNDING_YEAR_MIN_OPTIONS = [
		{ title: "Any", value: null },
		...Array.from({ length: CURRENT_YEAR - 1990 }, (_, i) => ({
			title: String(1991 + i),
			value: 1991 + i,
		})),
	];

	const FUNDING_YEAR_MAX_OPTIONS = FUNDING_YEAR_MIN_OPTIONS;

	const FUNDING_MIN_OPTIONS = [
		{ title: "Any", value: null },
		{ title: "$0", value: 0 },
		{ title: "$1M", value: 1_000_000 },
		{ title: "$5M", value: 5_000_000 },
		{ title: "$10M", value: 10_000_000 },
		{ title: "$25M", value: 25_000_000 },
		{ title: "$50M", value: 50_000_000 },
		{ title: "$100M", value: 100_000_000 },
		{ title: "$250M", value: 250_000_000 },
		{ title: "$500M", value: 500_000_000 },
	];

	const FUNDING_MAX_OPTIONS = [
		...FUNDING_MIN_OPTIONS.filter((o) => o.value !== 0),
		{ title: "$1B", value: 1_000_000_000 },
	];

	const handleSearch = async () => {
		try {
			const hasBounds = workforceMinPercent !== null || workforceMaxPercent !== null;

			if (hasBounds) {
				const missingPeriod = workforcePeriod === null;
				const missingDepartment = workforceDepartment === null;

				if (missingPeriod && missingDepartment) {
					setWorkforceError("Period and department are required when growth percentage is set");

					return;
				}

				if (missingPeriod) {
					setWorkforceError("Period is required when growth percentage is set");

					return;
				}

				if (missingDepartment) {
					setWorkforceError("Department is required when growth percentage is set");

					return;
				}
			}

			setWorkforceError(null);

			const result = await previewCompanySearch({
				query: query || undefined,
				employees: employees.length > 0 ? employees : undefined,
				keywords: keywords.length > 0 ? keywords : undefined,
				countries: countries.length > 0 ? countries : undefined,
				category: categories.length > 0 ? categories : undefined,
				type: type.length > 0 ? type : undefined,
				revenue: revenue.length > 0 ? revenue : undefined,
				fundingRounds: fundingRounds.length > 0 ? fundingRounds : undefined,
				industries: selectedIndustries.length > 0 ? selectedIndustries : undefined,
				foundedYear:
					foundedYearMin !== null || foundedYearMax !== null
						? { min: foundedYearMin, max: foundedYearMax }
						: undefined,
				fundingYear:
					fundingYearMin !== null || fundingYearMax !== null
						? { min: fundingYearMin, max: fundingYearMax }
						: undefined,
				fundingAmount:
					fundingAmountMin !== null || fundingAmountMax !== null
						? { min: fundingAmountMin, max: fundingAmountMax }
						: undefined,
				workforceGrowth:
					workforcePeriod !== null ||
					workforceDepartment !== null ||
					workforceMinPercent !== null ||
					workforceMaxPercent !== null
						? {
								period: workforcePeriod as "m6" | "y1" | "y2" | null,
								department: workforceDepartment as WorkforceDepartment | null,
								minPercent: workforceMinPercent,
								maxPercent: workforceMaxPercent,
							}
						: undefined,
			}).unwrap();

			setHasSearched(true);
			setResults(result.items);
		} catch (e) {
			console.error(e);
		}
	};

	const toggleCompany = (company: CompanySearchPreviewItem) => {
		setSelectedCompanies((prev) =>
			prev.some((c) => c.id === company.id)
				? prev.filter((c) => c.id !== company.id)
				: [...prev, company]
		);
	};

	const toNumber = (val: string): number | undefined => {
		const n = Number(val);

		return val.trim() !== "" && !isNaN(n) ? n : undefined;
	};

	const isCompanySelected = (company: CompanySearchPreviewItem) =>
		selectedCompanies.some((c) => c.id === company.id);

	const savePeople = async (data?: ManualCompany) => {
		if (addCompanyType === "enrich") {
			try {
				await addCompaniesToTracker({
					id: trackerId,
					companies: selectedCompanies.map((c) => ({ id: c.id ?? "" })),
				}).unwrap();
			} catch (e) {
				console.error(e);
			}
		} else if (addCompanyType === "manual" && data) {
			try {
				await addManualOrganizationToTracker({
					id: trackerId,
					name: data.name,
					websiteUrl: data.websiteUrl,
					linkedinUrl: data.linkedinUrl,
				}).unwrap();
			} catch (e) {
				console.error(e);
			}
		}

		setOpen(false);
	};

	const handleClose = () => {
		setOpen(false);
		setQuery("");
		setWorkforceError(null);
		setEmployees([]);
		setKeywords([]);
		setSelectedIndustries([]);
		setRegions([]);
		setWorkforcePeriod(null);
		setWorkforceDepartment(null);
		setWorkforceMinPercent(null);
		setWorkforceMaxPercent(null);
		setCountries([]);
		setCategories([]);
		setType([]);
		setRevenue([]);
		setFundingRounds([]);
		setFoundedYearMin(null);
		setFoundedYearMax(null);
		setFundingAmountMin(null);
		setFundingAmountMax(null);
		setFundingYearMin(null);
		setFundingYearMax(null);
		setResults([]);
		setHasSearched(false);
		setSelectedCompanies([]);
		setCurrentFormState(undefined);
		countryNameMapRef.current.clear();
		reset();
	};

	// Filter count helpers
	const countActiveFilters = useCallback(
		(fields: (string | number | null | undefined | unknown[])[]) => {
			return fields.reduce<number>((count, field) => {
				if (field === null || field === undefined || field === "") {
					return count;
				}

				if (Array.isArray(field)) {
					return count + (field.length > 0 ? 1 : 0);
				}

				return count + 1;
			}, 0);
		},
		[]
	);
	const companyDetailsCount = useMemo(
		() =>
			countActiveFilters([
				type,
				categories,
				employees,
				revenue,
				selectedIndustries,
				foundedYearMin,
				foundedYearMax,
			]),
		[
			type,
			categories,
			employees,
			revenue,
			selectedIndustries,
			countActiveFilters,
			foundedYearMin,
			foundedYearMax,
		]
	);

	const workforceGrowthCount = useMemo(
		() =>
			countActiveFilters([
				workforcePeriod,
				workforceDepartment,
				workforceMinPercent,
				workforceMaxPercent,
			]),
		[
			workforcePeriod,
			workforceDepartment,
			workforceMinPercent,
			workforceMaxPercent,
			countActiveFilters,
		]
	);

	const fundingTimelineCount = useMemo(
		() =>
			countActiveFilters([
				fundingAmountMin,
				fundingAmountMax,
				fundingYearMin,
				fundingYearMax,
				fundingRounds,
			]),
		[
			fundingAmountMin,
			fundingAmountMax,
			countActiveFilters,
			fundingYearMin,
			fundingYearMax,
			fundingRounds,
		]
	);
	const locationCount = useMemo(
		() => countActiveFilters([regions, countries]),
		[regions, countries, countActiveFilters]
	);

	const keywordsCount = useMemo(
		() => countActiveFilters([keywords]),
		[keywords, countActiveFilters]
	);

	return (
		<Modal
			disableSave={
				addCompanyType === "enrich" ? selectedCompanies.length === 0 : !currentFormState?.name
			}
			handleClose={handleClose}
			handleSave={addCompanyType === "enrich" ? () => savePeople() : handleSubmit(savePeople)}
			isLoading={addCompaniesIsLoading || addManualOrganizationIsLoading}
			isOpened={open}
			size="xl"
			submitButtonText="Save"
			title="Search for a company to add"
		>
			<div className="flex flex-col gap-4 w-full mb-4 ">
				<Radio
					isChecked={addCompanyType === "enrich"}
					labelClass="items-center"
					name="addCompany"
					value="enrich"
					onChange={() => setAddCompanyType("enrich")}
				>
					{ts("searchForCompanies")}
				</Radio>
				<Radio
					isChecked={addCompanyType === "manual"}
					labelClass="items-center"
					name="addCompany"
					value="manual"
					onChange={() => setAddCompanyType("manual")}
				>
					{ts("addCompanyManually")}
				</Radio>
			</div>

			{addCompanyType === "manual" && (
				<>
					<div className="flex flex-col gap-4 w-full mb-4">
						<InputField
							error={errors?.name?.message}
							handleChange={(event: ChangeEvent<HTMLInputElement>) =>
								handleSaveFormState([{ name: "name", value: event.target.value }])
							}
							label={ts("companyName")}
							name="manualCompanyName"
							showError={!!errors?.name?.message}
							value={currentFormState?.name ?? ""}
						/>
						<InputField
							error={errors?.websiteUrl?.message}
							handleChange={(event: ChangeEvent<HTMLInputElement>) =>
								handleSaveFormState([{ name: "websiteUrl", value: event.target.value }])
							}
							isOptional
							label="Website URL"
							name="manualCompanyWebsiteUrl"
							showError={!!errors?.websiteUrl?.message}
							value={currentFormState?.websiteUrl ?? ""}
						/>
						<InputField
							error={errors?.linkedinUrl?.message}
							handleChange={(event: ChangeEvent<HTMLInputElement>) =>
								handleSaveFormState([{ name: "linkedinUrl", value: event.target.value }])
							}
							isOptional
							label="LinkedIn URL"
							name="manualCompanyLinkedinUrl"
							showError={!!errors?.linkedinUrl?.message}
							value={currentFormState?.linkedinUrl ?? ""}
						/>
					</div>
					<div className="flex flex-col gap-4 w-full mb-4">
						{getAllErrors(addManualOrganizationError, formErrors).length
							? renderErrorMessages(getAllErrors(addManualOrganizationError, formErrors))
							: null}
					</div>
				</>
			)}

			{addCompanyType === "enrich" && (
				<div className="flex flex-row gap-4 max-h-[calc(100vh-350px)] min-h-0 overflow-x-hidden scrollbar-hide">
					<div
						className={classNames(
							"flex flex-col gap-4 min-h-0 overflow-y-auto [&::-webkit-scrollbar]:hidden",
							hasSearched ? "flex-1" : "w-full"
						)}
					>
						{/* Basic Search - Always Visible */}
						<div className="flex flex-col gap-1">
							<span className="text-sm font-medium">{ts("companyName")}</span>
							<SearchField
								handleChange={(event: ChangeEvent<HTMLInputElement>) =>
									setQuery(event.target.value)
								}
								name="searchCompany"
								placeholder={ts("companyNamePlaceholder")}
								value={query}
							/>
						</div>

						{/* Company Details - Collapsible */}
						<div
							className={classNames(
								"px-4 py-2 w-full items-start rounded-xl border border-border-light",
								"flex flex-col text-left",
								showCompanyDetails ? "bg-brand-white" : "bg-white"
							)}
						>
							<button
								className="w-full items-center justify-between group cursor-pointer flex flex-row text-left gap-6 mb-2"
								type="button"
								onClick={() => setShowCompanyDetails(!showCompanyDetails)}
							>
								<div className="flex flex-col">
									<div className="text-smd text-text font-bold flex items-center gap-2">
										{ts("companyFilters")}
										{companyDetailsCount > 0 && (
											<span className="inline-flex items-center justify-center rounded-full bg-blue-action px-2 py-0.5 text-xs font-medium text-white">
												{companyDetailsCount}
											</span>
										)}
									</div>
									<div className="text-ssm text-text-meta">
										{ts("companyFiltersDescriptionTracker")}
									</div>
								</div>
								<div
									className={classNames(
										"flex !w-[36px] !h-[36px] group-hover:bg-blue-action rounded-md items-center justify-center border",
										showCompanyDetails
											? "border-border-light bg-white"
											: "border-transparent bg-background-light"
									)}
								>
									<Icon
										className="text-text-meta group-hover:text-white !w-[12px] h-auto"
										icon={showCompanyDetails ? faChevronUp : faChevronDown}
									/>
								</div>
							</button>
							{showCompanyDetails && (
								<div className="flex flex-col gap-4 w-full">
									<div className="flex gap-2">
										<MultiAutoComplete
											stayOn
											data={COMPANY_TYPE_OPTIONS}
											handleSelect={(value) => setType(value?.map((r) => r.id.toString()) ?? [])}
											label={ts("companyTypeLabel")}
											placeholder={ts("companyTypePlaceholder")}
											selectedItems={type.map((r) => ({
												id: r,
												title: COMPANY_TYPE_OPTIONS.find((c) => c.id === r)?.title ?? r,
											}))}
										/>

										<MultiAutoComplete
											stayOn
											data={CATEGORY_OPTIONS}
											handleSelect={(value) =>
												setCategories(value?.map((r) => r.id.toString()) ?? [])
											}
											label={ts("companyCategoryLabel")}
											placeholder={ts("companyCategoryPlaceholder")}
											selectedItems={categories.map((r) => ({
												id: r,
												title: CATEGORY_OPTIONS.find((c) => c.id === r)?.title ?? r,
											}))}
										/>
									</div>

									<div className="flex gap-2">
										<MultiAutoComplete
											stayOn
											data={industryOptions}
											handleSelect={(value) => {
												const selectedNames = value?.map((r) => r.id) || [];
												const industries = (industriesData || []).filter((ind: Industry) =>
													selectedNames.includes(ind.name)
												);

												setSelectedIndustries(industries);
											}}
											label={ts("industriesLabel")}
											placeholder={ts("industriesPlaceholder")}
											selectedItems={selectedIndustries.map((ind) => ({
												id: ind.name,
												title: ind.name,
											}))}
										/>
									</div>

									<div className="flex gap-2">
										<MultiAutoComplete
											stayOn
											data={EMPLOYEE_OPTIONS}
											handleSelect={(value) =>
												setEmployees(value?.map((r) => r.id.toString()) ?? [])
											}
											label={ts("companySizeLabel")}
											placeholder={ts("companySizePlaceholder")}
											selectedItems={employees.map((r) => ({
												id: r,
												title: EMPLOYEE_OPTIONS.find((c) => c.id === r)?.title ?? r,
											}))}
										/>

										<MultiAutoComplete
											stayOn
											data={REVENUE_OPTIONS}
											handleSelect={(value) => setRevenue(value?.map((r) => r.id.toString()) ?? [])}
											label={ts("revenueLabel")}
											placeholder={ts("revenuePlaceholder")}
											selectedItems={revenue.map((r) => ({
												id: r,
												title: REVENUE_OPTIONS.find((c) => c.id === r)?.title ?? r,
											}))}
										/>
									</div>
									<div className="flex flex-col gap-1">
										<span className="text-sm font-medium">{ts("foundedYearLabel")}</span>
										<div className="flex gap-2 w-full">
											<div className="flex gap-2 items-center w-full">
												<SelectField
													name="foundedYearMin"
													selectedOption={foundedYearMin}
													handleSelect={(val) => setFoundedYearMin(val as number | null)}
													options={FOUNDING_YEAR_OPTIONS}
													hideEmptyValue
												/>
												<span className="text-sm shrink-0">to</span>
												<SelectField
													name="foundedYearMax"
													selectedOption={foundedYearMax}
													handleSelect={(val) => setFoundedYearMax(val as number | null)}
													options={FOUNDING_YEAR_OPTIONS}
													hideEmptyValue
												/>
											</div>
										</div>
									</div>
								</div>
							)}
						</div>

						{/* Funding & Timeline - Collapsible */}
						<div
							className={classNames(
								"px-4 py-2 w-full items-start rounded-xl border border-border-light",
								"flex flex-col text-left",
								showFundingTimeline ? "bg-brand-white" : "bg-white"
							)}
						>
							<button
								className="w-full items-center justify-between group cursor-pointer flex flex-row text-left gap-6 mb-2"
								type="button"
								onClick={() => setShowFundingTimeline(!showFundingTimeline)}
							>
								<div className="flex flex-col">
									<div className="text-smd text-text font-bold flex items-center gap-2">
										{ts("fundingFilters")}
										{fundingTimelineCount > 0 && (
											<span className="inline-flex items-center justify-center rounded-full bg-blue-action px-2 py-0.5 text-xs font-medium text-white">
												{fundingTimelineCount}
											</span>
										)}
									</div>
									<div className="text-ssm text-text-meta">{ts("fundingFiltersDescription")}</div>
								</div>
								<div
									className={classNames(
										"flex !w-[36px] !h-[36px] group-hover:bg-blue-action rounded-md items-center justify-center border",
										showFundingTimeline
											? "border-border-light bg-white"
											: "border-transparent bg-background-light"
									)}
								>
									<Icon
										className="text-text-meta group-hover:text-white !w-[12px] h-auto"
										icon={showFundingTimeline ? faChevronUp : faChevronDown}
									/>
								</div>
							</button>
							{showFundingTimeline && (
								<div className="flex flex-col gap-4 w-full">
									<div className="flex flex-col gap-1">
										<div className="flex gap-2">
											<MultiAutoComplete
												stayOn
												data={FUNDING_ROUNDS_OPTIONS}
												handleSelect={(value) =>
													setFundingRounds(value?.map((r) => r.id.toString()) ?? [])
												}
												label={ts("fundingRoundsLabel")}
												placeholder={ts("fundingRoundsPlaceholder")}
												selectedItems={fundingRounds.map((r) => ({
													id: r,
													title: FUNDING_ROUNDS_OPTIONS.find((c) => c.id === r)?.title ?? r,
												}))}
											/>
										</div>
									</div>
									<div className="flex flex-col gap-1 w-full">
										<span className="text-sm font-medium">{ts("fundingAmountLabel")}</span>

										<div className="flex flex-col gap-1">
											<div className="flex gap-2 items-center">
												<SelectField
													name="fundingAmountMin"
													selectedOption={fundingAmountMin}
													handleSelect={(val) => setFundingAmountMin(val as number | null)}
													options={FUNDING_MIN_OPTIONS}
													hideEmptyValue
												/>
												<span className="text-sm shrink-0">to</span>
												<SelectField
													name="fundingAmountMax"
													selectedOption={fundingAmountMax}
													handleSelect={(val) => setFundingAmountMax(val as number | null)}
													options={FUNDING_MAX_OPTIONS}
													hideEmptyValue
												/>
											</div>
										</div>
									</div>

									<div className="flex flex-col gap-1">
										<span className="text-sm font-medium">{ts("fundingYearLabel")}</span>
										<div className="flex flex-col gap-1">
											<div className="flex gap-2 items-center">
												<SelectField
													name="fundingYearMin"
													selectedOption={fundingYearMin}
													handleSelect={(val) => setFundingYearMin(val as number | null)}
													options={FUNDING_YEAR_MIN_OPTIONS}
													hideEmptyValue
												/>
												<span className="text-sm shrink-0">to</span>
												<SelectField
													name="fundingYearMax"
													selectedOption={fundingYearMax}
													handleSelect={(val) => setFundingYearMax(val as number | null)}
													options={FUNDING_YEAR_MAX_OPTIONS}
													hideEmptyValue
												/>
											</div>
										</div>
									</div>
								</div>
							)}
						</div>

						{/* Location - Collapsible */}
						<div
							className={classNames(
								"px-4 py-2 w-full items-start rounded-xl border border-border-light",
								"flex flex-col text-left",
								showLocation ? "bg-brand-white" : "bg-white"
							)}
						>
							<button
								className="w-full items-center justify-between group cursor-pointer flex flex-row text-left gap-6 mb-2"
								type="button"
								onClick={() => setShowLocation(!showLocation)}
							>
								<div className="flex flex-col">
									<div className="text-smd text-text font-bold flex items-center gap-2">
										{ts("locationFilters")}
										{locationCount > 0 && (
											<span className="inline-flex items-center justify-center rounded-full bg-blue-action px-2 py-0.5 text-xs font-medium text-white">
												{locationCount}
											</span>
										)}
									</div>
									<div className="text-ssm text-text-meta">
										{ts("locationFiltersDescriptionTracker")}
									</div>
								</div>
								<div
									className={classNames(
										"flex !w-[36px] !h-[36px] group-hover:bg-blue-action rounded-md items-center justify-center border",
										showLocation
											? "border-border-light bg-white"
											: "border-transparent bg-background-light"
									)}
								>
									<Icon
										className="text-text-meta group-hover:text-white !w-[12px] h-auto"
										icon={showLocation ? faChevronUp : faChevronDown}
									/>
								</div>
							</button>
							{showLocation && (
								<div className="flex flex-col gap-4 w-full">
									<MultiAutoComplete
										stayOn
										data={regionOptions}
										handleSelect={handleRegionSelect}
										label={ts("regionsLabel")}
										placeholder={ts("regionsPlaceholder")}
										selectedItems={regions.map((r) => ({ id: r, title: r }))}
									/>

									<CompanyEnrichAutoComplete
										stayOn
										fetchFn={fetchCompanyCountries}
										handleSelect={handleCountrySelect}
										label={ts("companyCountriesLabel")}
										placeholder={ts("companyCountriesPlaceholder")}
										selectedItems={countries.map((r) => ({
											id: r,
											title: countryNameMapRef.current?.get(r) || r,
										}))}
									/>
								</div>
							)}
						</div>

						{/* Keywords - Collapsible */}
						<div
							className={classNames(
								"px-4 py-2 w-full items-start rounded-xl border border-border-light",
								"flex flex-col text-left",
								showKeywords ? "bg-brand-white" : "bg-white"
							)}
						>
							<button
								className="w-full items-center justify-between group cursor-pointer flex flex-row text-left gap-6 mb-2"
								type="button"
								onClick={() => setShowKeywords(!showKeywords)}
							>
								<div className="flex flex-col">
									<div className="text-smd text-text font-bold flex items-center gap-2">
										{ts("keywordsLabel")}
										{keywordsCount > 0 && (
											<span className="inline-flex items-center justify-center rounded-full bg-blue-action px-2 py-0.5 text-xs font-medium text-white">
												{keywordsCount}
											</span>
										)}
									</div>
									<div className="text-ssm text-text-meta">{ts("keywordsDescription")}</div>
								</div>
								<div
									className={classNames(
										"flex !w-[36px] !h-[36px] group-hover:bg-blue-action rounded-md items-center justify-center border",
										showKeywords
											? "border-border-light bg-white"
											: "border-transparent bg-background-light"
									)}
								>
									<Icon
										className="text-text-meta group-hover:text-white !w-[12px] h-auto"
										icon={showKeywords ? faChevronUp : faChevronDown}
									/>
								</div>
							</button>
							{showKeywords && (
								<MultiAutoCompleteApi
									noData
									handleSelect={(value?: MultiAutoCompleteApiItem[]) =>
										setKeywords(value?.map((r) => r.id.toString()) ?? [])
									}
									label={ts("keywordsLabel")}
									placeholder={ts("keywordsPlaceholder")}
									propertyName="title"
									selectedItems={keywords.map((r) => ({ id: r, title: r }))}
								/>
							)}
						</div>

						<div
							className={classNames(
								"px-4 py-2 w-full items-start rounded-xl border border-border-light",
								"flex flex-col text-left",
								showWorkforceGrowth ? "bg-brand-white" : "bg-white"
							)}
						>
							<button
								className="w-full items-center justify-between group cursor-pointer flex flex-row text-left gap-6 mb-2"
								type="button"
								onClick={() => setShowWorkforceGrowth(!showWorkforceGrowth)}
							>
								<div className="flex flex-col">
									<div className="text-smd text-text font-bold flex items-center gap-2">
										{ts("workforceGrowthFilters")}
										{workforceGrowthCount > 0 && (
											<span className="inline-flex items-center justify-center rounded-full bg-blue-action px-2 py-0.5 text-xs font-medium text-white">
												{workforceGrowthCount}
											</span>
										)}
									</div>
									<div className="text-ssm text-text-meta">
										{ts("workforceGrowthFiltersDescription")}
									</div>
								</div>
								<div
									className={classNames(
										"flex !w-[36px] !h-[36px] group-hover:bg-blue-action rounded-md items-center justify-center border",
										showWorkforceGrowth
											? "border-border-light bg-white"
											: "border-transparent bg-background-light"
									)}
								>
									<Icon
										className="text-text-meta group-hover:text-white !w-[12px] h-auto"
										icon={showWorkforceGrowth ? faChevronUp : faChevronDown}
									/>
								</div>
							</button>
							{showWorkforceGrowth && (
								<div className="flex flex-col gap-4 w-full">
									<SelectField
										label={ts("workforcePeriodLabel")}
										name="workforcePeriod"
										selectedOption={workforcePeriod}
										handleSelect={(val) => {
											setWorkforcePeriod(val as string | null);
											setWorkforceError(null);
										}}
										options={WORKFORCE_PERIOD_OPTIONS}
										hideEmptyValue
									/>
									<SelectField
										label={ts("workforceDepartmentLabel")}
										name="workforceDepartment"
										selectedOption={workforceDepartment}
										handleSelect={(val) => {
											setWorkforceDepartment(val as string | null);
											setWorkforceError(null);
										}}
										options={WORKFORCE_DEPARTMENT_OPTIONS}
										hideEmptyValue
									/>
									<div className="flex flex-col gap-1">
										<div className="flex flex-row items-end gap-4 w-full mb-4">
											<div className="flex flex-col gap-2 w-full mb-4 ">
												<div className="flex justify-between">
													<label className="block text-sm font-medium text-gray-900 ">
														{ts("workforceGrowthLabel")}
													</label>
													<span
														className=" text-sm underline text-gray-700 cursor-pointer"
														onClick={() => {
															setWorkforceMaxPercent(null);
															setWorkforceMinPercent(null);
														}}
													>
														Clear
													</span>
												</div>
												<div className="flex !items-center gap-5">
													{
														<TextField
															value={workforceMinPercent ?? ""}
															placeholder="Min"
															size="small"
															onChange={(e) => {
																const raw = e.target.value;
																const val =
																	raw === ""
																		? null
																		: Math.min(
																				Math.max(Number(raw), -100),
																				(workforceMaxPercent ?? 100) - 1
																			);

																setWorkforceMinPercent(val);
																setWorkforceError(null);
															}}
															slotProps={{
																htmlInput: { step: 1, min: -100, max: 100, type: "number" },
																input: {
																	endAdornment: (
																		<span
																			style={{ fontSize: 12, color: "var(--color-text-secondary)" }}
																		>
																			%
																		</span>
																	),
																},
															}}
															sx={{
																width: 80,
																"& .MuiOutlinedInput-root": {
																	borderRadius: "8px",
																	fontSize: 13,
																	"& fieldset": { borderColor: "var(--color-border-tertiary)" },
																	"&:hover fieldset": {
																		borderColor: "var(--color-border-secondary)",
																	},
																	"&.Mui-focused fieldset": {
																		borderColor: "#4d66f5",
																		borderWidth: 1,
																	},
																},
																"& input": { padding: "6px 8px", textAlign: "center" },
																"& input::-webkit-inner-spin-button, & input::-webkit-outer-spin-button":
																	{
																		display: "none",
																	},
															}}
														/>
													}
													<Slider
														value={[workforceMinPercent ?? -100, workforceMaxPercent ?? 100]}
														onChange={(_, newValue) => {
															const [min, max] = newValue as [number, number];

															setWorkforceMinPercent(min);
															setWorkforceMaxPercent(max);
															setWorkforceError(null);
														}}
														min={-100}
														max={100}
														step={1}
														valueLabelDisplay="auto"
														valueLabelFormat={(v) => `${v}%`}
														disableSwap
														sx={{ flex: 1 }}
													/>
													<TextField
														value={workforceMaxPercent ?? ""}
														size="small"
														placeholder="Max"
														onChange={(e) => {
															const raw = e.target.value;
															const val =
																raw === ""
																	? null
																	: Math.max(
																			Math.min(Number(raw), 100),
																			(workforceMinPercent ?? -100) + 1
																		);

															setWorkforceMaxPercent(val);
															setWorkforceError(null);
														}}
														slotProps={{
															htmlInput: { step: 1, min: -100, max: 100, type: "number" },
															input: {
																endAdornment: (
																	<span
																		style={{ fontSize: 12, color: "var(--color-text-secondary)" }}
																	>
																		%
																	</span>
																),
															},
														}}
														sx={{
															width: 80,
															"& .MuiOutlinedInput-root": {
																borderRadius: "8px",
																fontSize: 13,
																"& fieldset": { borderColor: "var(--color-border-tertiary)" },
																"&:hover fieldset": {
																	borderColor: "var(--color-border-secondary)",
																},
																"&.Mui-focused fieldset": {
																	borderColor: "#4d66f5",
																	borderWidth: 1,
																},
															},
															"& input": { padding: "6px 8px", textAlign: "center" },
															"& input::-webkit-inner-spin-button, & input::-webkit-outer-spin-button":
																{
																	display: "none",
																},
														}}
													/>
												</div>
											</div>
										</div>

										{workforceError && (
											<p className="text-xs text-status-error">{workforceError}</p>
										)}
									</div>
								</div>
							)}
						</div>

						{/* Search Button */}
						<div className="flex justify-end">
							<div className="w-fit">
								<Button
									color={ButtonColor.ACTION_SECONDARY}
									isLoading={isSearchLoading}
									title={ts("search")}
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										handleSearch();
									}}
								/>
							</div>
						</div>
					</div>
					<div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide">
						{/* Results */}
						{hasSearched && (
							<div className="flex-1 min-h-0 overflow-y-auto ">
								{results.length > 0 && (
									<>
										<div className="flex items-center justify-between">
											<span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
												{ts("results")}
											</span>
											{selectedCompanies.length > 0 && (
												<span className="text-xs text-gray-500">
													{selectedCompanies.length} {ts("selected")}
												</span>
											)}
										</div>
										<div className="flex flex-col gap-2">
											{results.map((company) => (
												<CompanyRow
													ts={ts}
													key={`result-${company.id}`}
													company={company}
													isSelected={isCompanySelected(company)}
													onToggle={toggleCompany}
												/>
											))}
										</div>
									</>
								)}
								{results.length === 0 && !isSearchLoading && (
									<p className="text-sm text-gray-500 text-center py-4">{ts("noResults")}</p>
								)}
							</div>
						)}
					</div>
				</div>
			)}
		</Modal>
	);
};

const CompanyRow = ({
	company,
	isSelected,
	onToggle,
	ts,
}: {
	company: CompanySearchPreviewItem;
	ts: (key: string) => string;
	isSelected: boolean;
	onToggle: (company: CompanySearchPreviewItem) => void;
}) => {
	const [expanded, setExpanded] = useState(false);
	const description = company.description ?? "";
	const isLong = description.length > 250;

	const [imgError, setImgError] = useState(false);

	const toSafeUrl = (url: string): string | null => {
		try {
			const parsed = new URL(url.startsWith("http") ? url : `https://${url}`);

			return ["http:", "https:"].includes(parsed.protocol) ? parsed.href : null;
		} catch {
			return null;
		}
	};

	return (
		<div
			className={classNames(
				"w-full border rounded-xl flex flex-row items-start cursor-pointer hover:bg-gray-50/30 p-2.5 transition-colors",
				isSelected ? "border-blue-action bg-blue-50" : "border-gray-200"
			)}
			onClick={() => onToggle(company)}
		>
			<div className="flex items-start w-full gap-3">
				{company.logo_url && !imgError ? (
					<div className="w-[40px] h-[40px]">
						<img
							className="w-[40px] h-[40px] object-cover rounded-full shrink-0"
							src={company.logo_url}
							onError={() => setImgError(true)}
						/>
					</div>
				) : (
					<div className="w-[40px] h-[40px] flex items-center justify-center bg-brand-white border-brand-light border text-brand-light font-semibold text-md rounded-full shrink-0">
						{company.name?.charAt(0)?.toUpperCase() ?? "?"}
					</div>
				)}
				<div className="flex flex-col min-w-0 flex-1">
					<div className="flex flex-row min-w-0 gap-2 items-center justify-between">
						<div className="flex  gap-2 items-center">
							<span className="text-sm font-semibold truncate">{company.name ?? "—"}</span>
							{(() => {
								const safeUrl = company.website ? toSafeUrl(company.website) : null;

								return safeUrl ? (
									<Link href={safeUrl} target="_blank" onClick={(e) => e.stopPropagation()}>
										<ExternalLink />
									</Link>
								) : null;
							})()}
						</div>
						<span className="inline-flex w-fit items-center px-2 py-0 rounded-full text-[10px] font-medium bg-light-blue text-blue-action">
							{company.industry ?? company.domain}
						</span>
					</div>
					{description && (
						<div>
							<span className="font-medium text-primary-gray text-[12px] leading-[20px]">
								{expanded || !isLong ? description : `${description.slice(0, 250)}...`}
							</span>
							{isLong && (
								<button
									className="ml-1 text-[12px]  text-blue-action font-medium hover:underline shrink-0"
									type="button"
									onClick={(e) => {
										e.stopPropagation();
										setExpanded((prev) => !prev);
									}}
								>
									{expanded ? ts("showLess") : ts("showMore")}
								</button>
							)}
						</div>
					)}
				</div>
				<div
					className={classNames(
						"w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5",
						isSelected ? "border-blue-action bg-blue-action" : "border-gray-300"
					)}
				>
					{isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
				</div>
			</div>
		</div>
	);
};
